import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import {
  Button, Modal, ModalHeader, ModalBody, ModalFooter,
  FormGroup, Label, Input, CustomInput
} from 'reactstrap';
import moment from 'moment';
import { useAuth } from '../Auth';
import generateAIEmailDraft from '../../lib/fetch/aiDraft';
import fetchEmailTemplates from '../../lib/fetch/emailTemplates';
import { EMAIL_LANGUAGES, EMAIL_TONES } from '../../constants/emailScenarios';
import ContextSummary from './ContextSummary';
import { getDefaultScenario, getScenarioOptions } from './scenarios';
import resolvePlaceholders from './placeholders';

const buildContextFromSubject = (subject = {}) => {
  const { kind = 'deal', record = {} } = subject;

  if (kind === 'contact') {
    const fullName = [record.first_name, record.last_name].filter(Boolean).join(' ');

    return {
      deal: {
        name: fullName || record.full_name || '',
        stage: record.stage || '',
        amount: 0,
        closing_date: record.closing_date || '',
        success_rate: record.success_rate || 0,
      },
      company: {
        name: record.company_name || '',
      },
      contact: {
        first_name: record.first_name || '',
        last_name: record.last_name || '',
        full_name: fullName || record.full_name || '',
        email: record.email || '',
      },
      recipientEmail: record.email || '',
    };
  }

  if (kind === 'company') {
    return {
      deal: {
        name: record.name || '',
        stage: record.stage || '',
        amount: record.amount || 0,
        closing_date: record.closing_date || '',
        success_rate: record.success_rate || 0,
      },
      company: {
        name: record.name || '',
      },
      contact: {
        full_name: record.contact_name || '',
        email: record.email || '',
      },
      recipientEmail: record.email || '',
    };
  }

  return {
    deal: {
      name: record.name || '',
      stage: record.stage || '',
      amount: record.amount || 0,
      closing_date: record.closing_date || '',
      success_rate: record.success_rate || 0,
    },
    company: {
      name: record.company_name || '',
    },
    contact: {
      full_name: record.contact_name || '',
      email: record.contact_email || '',
    },
    recipientEmail: record.contact_email || '',
  };
};

function AIEmailModal({ open, toggle, subject, activities, profile, onSaveActivity }) {
  const [t, i18n] = useTranslation('AI_EMAIL_ASSISTANT');
  const commonT = useTranslation('COMMON')[0];
  const alert = useAlert();
  const [{ token } = {}] = useAuth();
  const normalized = buildContextFromSubject(subject);
  const { deal, company, contact, recipientEmail } = normalized;
  const [scenario, setScenario] = useState(getDefaultScenario(deal.stage));
  const [templateId, setTemplateId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [tone, setTone] = useState('professional');
  const [language, setLanguage] = useState(i18n.language === 'en' ? 'en' : 'it');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (open) {
      setScenario(getDefaultScenario(deal.stage));
      setTemplateId(null);
      setLanguage(i18n.language === 'en' ? 'en' : 'it');
    }
  }, [open, deal.stage, i18n.language]);

  useEffect(() => {
    const loadTemplates = async () => {
      if (!open || !token) return;

      const { error, data } = await fetchEmailTemplates({ token, limit: 1000, order: 'updated_at', sort: 'desc' });
      if (!error) {
        setTemplates(data || []);
      }
    };

    loadTemplates();
  }, [open, token]);

  const selectedTemplate = templates.find(template => template.id === templateId);
  const scenarioValue = selectedTemplate ? `template:${selectedTemplate.id}` : scenario;

  const context = {
    deal,
    company,
    contact,
    user: {
      full_name: profile && `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
    },
    recent_activities: activities.slice(0, 3).map(({ type, date, description }) => ({ type, date, description })),
  };

  const generateDraft = async () => {
    setLoading(true);
    const resolvedNotes = resolvePlaceholders(notes, context);
    const { error, data } = await generateAIEmailDraft({
      token,
      data: {
        scenario: selectedTemplate?.scenario || scenario,
        tone,
        language,
        notes: resolvedNotes,
        context,
        template_id: templateId,
      },
    });
    setLoading(false);

    if (error) {
      alert.error(commonT('GENERIC_ERROR'));
      return;
    }

    setDraft(data);
  };

  const copyDraft = async () => {
    const text = `${t('SUBJECT')}: ${draft.subject}\n\n${draft.body}`;
    await navigator.clipboard.writeText(text);
    alert.success(t('COPIED'));
  };

  const openMailApp = () => {
    if (!recipientEmail) return;
    window.location.href = `mailto:${recipientEmail}?subject=${encodeURIComponent(draft.subject)}&body=${encodeURIComponent(draft.body)}`;
  };

  const saveAsActivity = async () => {
    if (!onSaveActivity) return;
    await onSaveActivity({
      date: moment().format('YYYY-MM-DD'),
      time: moment().format('HH:mm'),
      description: `${draft.subject}\n\n${draft.body}`,
      done: 1,
      user_id: profile.id,
    }, 'email');
    toggle();
  };

  const closeModal = () => {
    setDraft(null);
    setNotes('');
    setTemplateId(null);
    toggle();
  };

  return (
    <Modal isOpen={open} fade={false} toggle={closeModal} size="lg" className="ai-email-modal">
      <ModalHeader toggle={closeModal}>
        {t('TITLE')}
        <span>{t('DRAFTING_FOR', { company: company.name || '-', contact: contact.full_name || '-', deal: deal.name || '-' })}</span>
      </ModalHeader>
      <ModalBody>
        {!draft ? (
          <div>
            <FormGroup>
              <Label>{t('SCENARIO')}</Label>
              <CustomInput type="select" id="ai-email-scenario" value={scenarioValue} onChange={ev => {
                const value = ev.target.value;
                if (value.startsWith('template:')) {
                  const id = Number(value.replace('template:', ''));
                  const found = templates.find(template => template.id === id);
                  setTemplateId(id);
                  if (found?.scenario) setScenario(found.scenario);
                  return;
                }

                setTemplateId(null);
                setScenario(value);
              }}>
                <optgroup label={t('BUILT_IN_SCENARIOS')}>
                  {getScenarioOptions().map(option => <option key={option} value={option}>{t(`SCENARIOS.${option}`)}</option>)}
                </optgroup>
                <optgroup label={t('SAVED_TEMPLATES')}>
                  {templates.length > 0 ? templates.map(template => (
                    <option key={template.id} value={`template:${template.id}`}>
                      {template.name}
                    </option>
                  )) : <option disabled value="">{t('NO_TEMPLATES')}</option>}
                </optgroup>
              </CustomInput>
            </FormGroup>
            <FormGroup>
              <Label>{t('TONE')}</Label>
              <div className="ai-email-options">
                {EMAIL_TONES.map(option => (
                  <CustomInput key={option} type="radio" id={`ai-tone-${option}`} name="ai-tone" checked={tone === option} onChange={() => setTone(option)} label={t(`TONES.${option}`)} />
                ))}
              </div>
            </FormGroup>
            <FormGroup>
              <Label>{t('LANGUAGE')}</Label>
              <div className="ai-email-options">
                {EMAIL_LANGUAGES.map(option => (
                  <CustomInput key={option} type="radio" id={`ai-language-${option}`} name="ai-language" checked={language === option} onChange={() => setLanguage(option)} label={t(`LANGUAGES.${option}`)} />
                ))}
              </div>
            </FormGroup>
            <FormGroup>
              <Label>{t('NOTES')}</Label>
              <Input type="textarea" value={notes} onChange={ev => setNotes(ev.target.value)} placeholder={t('NOTES_PLACEHOLDER')} />
            </FormGroup>
            <ContextSummary deal={deal} activities={activities} />
          </div>
        ) : (
          <div className="ai-email-draft">
            <FormGroup>
              <Label>{t('SUBJECT')}</Label>
              <Input value={draft.subject} onChange={ev => setDraft({ ...draft, subject: ev.target.value })} />
            </FormGroup>
            <FormGroup>
              <Label>{t('BODY')}</Label>
              <Input type="textarea" value={draft.body} onChange={ev => setDraft({ ...draft, body: ev.target.value })} />
            </FormGroup>
            <Button color="secondary" onClick={generateDraft} disabled={loading}>{t('REGENERATE')}</Button>
            <Button color="link" onClick={() => setDraft(null)}>{t('TWEAK')}</Button>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        {!draft ? (
          <>
            <Button color="secondary" onClick={closeModal}>{commonT('CANCEL')}</Button>
            <Button color="primary" onClick={generateDraft} disabled={loading}>{loading ? t('GENERATING') : t('GENERATE')}</Button>
          </>
        ) : (
          <>
            <Button color="secondary" onClick={copyDraft}>{t('COPY')}</Button>
            <Button color="secondary" onClick={openMailApp} disabled={!recipientEmail}>{t('OPEN_MAIL')}</Button>
            {onSaveActivity && <Button color="primary" onClick={saveAsActivity}>{t('SAVE_ACTIVITY')}</Button>}
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}

AIEmailModal.propTypes = {
  open: PropTypes.bool,
  toggle: PropTypes.func.isRequired,
  subject: PropTypes.shape({}).isRequired,
  activities: PropTypes.arrayOf(PropTypes.shape({})),
  profile: PropTypes.shape({}),
  onSaveActivity: PropTypes.func,
};

AIEmailModal.defaultProps = {
  open: false,
  activities: [],
  profile: {},
};

export default AIEmailModal;
